/**
 * autoSync — ONE app-wide template revalidation ticker (Wave-2 Task 5).
 *
 * Before this, template changes propagated ONLY on a tab remount or a manual
 * Refresh/Sync click: `syncRegistry(since)` incremental pull was never exercised
 * automatically, so a rename/new-version made on another device stayed invisible
 * until the operator happened to reload. This ticker fixes that.
 *
 * Contract:
 *  - `startAutoSync(client)` (after login) → an IMMEDIATE tick, then every 60s,
 *    AND on `window` focus + `document` visibilitychange→visible. One instance
 *    app-wide (a re-entrant `startAutoSync` is a no-op while running).
 *  - Each tick fetches `listTemplates()` and notifies `subscribeTemplates(cb)`
 *    subscribers ONLY when the list actually CHANGED — compared via a cheap
 *    fingerprint (ids + currentVersion ids/status/version + name/code/previewRef).
 *  - Version-aware baked rehydrate: on a changed delivery, for templates this
 *    device has SYNCED whose `currentVersion.versionId` moved past the locally
 *    hydrated one, re-run the existing `hydrateBakedAssets` path — for those
 *    templates ONLY (never a bulk download of the whole catalog).
 *  - Tick failures are silent-but-LOGGED (offline is normal); the next tick
 *    retries. `stopAutoSync()` (logout) tears everything down — no sync while
 *    logged out.
 *  - Exposes `getLastSyncAt()` for the quiet freshness indicator.
 *
 * Cross-device latency bound: ≤60s while the app is open, and effectively
 * immediate when the window regains focus / becomes visible.
 */
import type { PlatformIO, ProductTemplate } from "@genz/platform";
import { hydrateBakedAssets, ioHydrateDeps, hasAnyBakedView, type BakedAssetRefsMap } from "../templates/bakedSync.js";
import { announceBakeAbsence } from "../templates/bakeDiagnostics.js";
import { listSyncedTemplates, saveSyncedTemplate } from "../templates/localTemplates.js";
import { hasMockupStored } from "../stores/mockupStore.js";
import { hydrationFailure } from "../stores/hydrationStore.js";

/** The minimal client surface the ticker needs (structural — keeps it trivially testable). */
export interface AutoSyncClient {
  listTemplates(query?: Record<string, string>): Promise<ProductTemplate[]>;
  getTemplate(id: string): Promise<ProductTemplate>;
}

/** The minimal logger surface (satisfied by `AppLogger`/`ClientLogger`). Optional. */
export interface AutoSyncLogger {
  breadcrumb(category: string, message: string, data?: Record<string, unknown>): void;
  log(level: string, message: string, ctx?: Record<string, unknown>): void;
}

/** The storage slice the baked-rehydrate seam needs. */
type SyncIO = Pick<PlatformIO, "localStore" | "decodeImage">;

/** A delivered template-list change. `at` is the epoch-ms of the tick that produced it. */
export interface TemplateSyncDelivery {
  templates: ProductTemplate[];
  at: number;
}
export type TemplateSubscriber = (delivery: TemplateSyncDelivery) => void;

export interface StartAutoSyncOptions {
  /** Enables the version-aware baked rehydrate for SYNCED templates. */
  io?: SyncIO;
  logger?: AutoSyncLogger;
  /** Ticker cadence (default 60s). Exposed for tests. */
  intervalMs?: number;
}

// --- Module-wide singleton state (ONE ticker app-wide) ---------------------
const subscribers = new Set<TemplateSubscriber>();
/** Sync-activity subscribers (freshness indicator) — notified on every `syncing` transition. */
const syncStateSubscribers = new Set<() => void>();
let running = false;
/** True WHILE a revalidation tick is in flight (drives the freshness spinner). */
let syncing = false;
let client: AutoSyncClient | null = null;
let io: SyncIO | undefined;
let logger: AutoSyncLogger | undefined;
let intervalId: ReturnType<typeof setInterval> | null = null;
/** Cheap change-detector for the last delivered list; `null` before the first tick. */
let lastFingerprint: string | null = null;
let lastSyncAt: number | null = null;
/** Guards against overlapping ticks (a slow network call + a focus event). */
let ticking = false;
/**
 * Which ticker OWNS the current `ticking` lock. Bumped by every stop, so a tick left running
 * by a torn-down ticker can neither hold the lock hostage nor release the live one's: the flag
 * was a bare boolean cleared in a `finally`, so a tick that never settled (a stalled request
 * the fetch layer hasn't given up on) kept `ticking` true FOREVER — through logout and back
 * in — and every later tick returned at the guard. Auto-sync was then silently dead for the
 * rest of the process, with the freshness indicator stuck on. A lock must not outlive its
 * ticker, and a settling stale tick must not touch state that is no longer its own.
 */
let tickOwner = 0;
/** A tick asked for WHILE one was in flight, coalesced to run once the current one settles.
 *  Dropping it lost the whole point of the focus/visibility ticks — "look now" waited out the
 *  rest of the 60s cycle whenever it landed on a slow tick. */
let pendingTick: string | null = null;
/** Consecutive failed ticks — escalates a persistent outage to ONE warn (see runTick). */
let consecutiveTickFailures = 0;
const TICK_FAILURE_WARN_AFTER = 3;

/** A stable, order-independent fingerprint of the change-relevant fields. */
function fingerprintTemplates(list: ProductTemplate[]): string {
  return list
    .map((t) => {
      const v = t.currentVersion;
      return [
        t.templateId,
        v?.versionId ?? "",
        v?.status ?? "",
        v?.versionNumber ?? "",
        v?.activeFrom ?? "",
        // Write token: bumps when the version's CONTENT changes under the same id (e.g. a
        // re-bake) — without it such a change never tripped the fingerprint, so the
        // reconcile pass below never ran for it.
        v?.versionToken ?? "",
        t.name ?? "",
        t.code ?? "",
        t.previewRef ?? "",
      ].join("\u001f"); // Unit Separator: delimited so adjacent fields can't collide
    })
    .sort()
    .join("\u001e"); // Record Separator: delimited so row boundaries can't merge
}

/**
 * Re-hydrate the baked layered-mockup assets for the SYNCED templates whose current
 * version moved past the locally hydrated one. Downloads happen ONLY for templates this
 * device already synced (in the CustomerTemplates sync list) — never the whole catalog.
 * Returns how many templates were re-hydrated. Best-effort per template.
 */
/**
 * Per-template failure streaks for the baked-asset download.
 *
 * The ticker runs every 60s AND on every window focus. An asset that stays broken therefore
 * produced one identical error line PER TEMPLATE PER TICK — in the operator's log that is
 * hundreds of rows that bury every other event and add no information after the first. The
 * project's own logging rule says a recurring failure logs ONCE and then escalates; this
 * implements it for this path.
 *
 * Reported: the first failure, then at 5 and 25 consecutive failures (so a persistent outage
 * still leaves a trail with a count), and recovery. Everything in between is a breadcrumb.
 */
const assetFailureStreak = new Map<string, { count: number; error: string }>();

/** Escalation points. A streak reports at its 1st, 5th and 25th consecutive failure. */
const ESCALATE_AT = new Set([1, 5, 25]);

export function noteAssetFailure(
  log: AutoSyncLogger | undefined,
  templateId: string,
  info: { trigger: string; error: string },
): void {
  const prev = assetFailureStreak.get(templateId);
  // A CHANGED error is new information even mid-streak (404 → CORS → offline are different
  // problems), so it re-reports rather than being swallowed by the streak.
  const errorChanged = !!prev && prev.error !== info.error;
  const count = (prev?.count ?? 0) + 1;
  assetFailureStreak.set(templateId, { count, error: info.error });
  if (!ESCALATE_AT.has(count) && !errorChanged) {
    log?.breadcrumb("sync", "autoSync: baked-asset download still failing", {
      templateId, consecutiveFailures: count,
    });
    return;
  }
  log?.log(
    "warn",
    "autoSync: the template record updated but its baked-asset download FAILED — renders on " +
      "this device stay design-only until it succeeds",
    {
      templateId,
      // The ACTUAL cause, naming the URL and status. This used to be a guess in prose
      // ("check R2 CORS / connectivity") because the error had been thrown away.
      error: info.error,
      trigger: info.trigger,
      consecutiveFailures: count,
    },
  );
}

/** A template that downloads successfully again ends its streak (and says so, once). */
export function clearAssetFailure(log: AutoSyncLogger | undefined, templateId: string): void {
  const prev = assetFailureStreak.get(templateId);
  if (!prev) return;
  assetFailureStreak.delete(templateId);
  log?.log("info", "autoSync: baked-asset download RECOVERED", {
    templateId, afterConsecutiveFailures: prev.count,
  });
}

export async function rehydrateChangedBaked(
  storage: SyncIO,
  syncClient: AutoSyncClient,
  fresh: ProductTemplate[],
  log?: AutoSyncLogger,
): Promise<number> {
  let local: ProductTemplate[];
  try {
    local = await listSyncedTemplates(storage);
  } catch {
    return 0; // no local store / unreadable → nothing to reconcile
  }
  if (local.length === 0) return 0;
  const localById = new Map(local.map((t) => [t.templateId, t]));
  let count = 0;
  for (const row of fresh) {
    const localRow = localById.get(row.templateId);
    if (!localRow) {
      // NOT synced on this device → skip (never bulk-download). WS-P: the POLICY is unchanged
      // and deliberate — healing here would pull gigabytes nobody asked for. What changes is
      // that device B's log can now distinguish this from "synced but the bake never arrived",
      // which used to look identical (AUD-8 LB-8 (iii)). Deduped per (version, cause), because
      // this loop runs on a 60 s ticker over the whole catalog.
      announceBakeAbsence(log, "not-synced", {
        templateId: row.templateId,
        versionId: row.currentVersion?.versionId,
        templateName: row.name,
      });
      continue;
    }
    const freshVer = row.currentVersion?.versionId ?? "";
    const localVer = localRow.currentVersion?.versionId ?? "";
    const versionMoved = !!freshVer && freshVer !== localVer;
    // Same version id but the version's WRITE TOKEN moved (the server bumps it on every
    // version write, incl. set_baked_assets) → the content changed under the same id; the
    // old version-only check made a re-bake invisible to other devices forever. Both tokens
    // must be non-empty (older servers/records don't carry it — then this check no-ops).
    const freshTok = row.currentVersion?.versionToken ?? "";
    const localTok = localRow.currentVersion?.versionToken ?? "";
    const contentMoved = !versionMoved && !!freshTok && !!localTok && freshTok !== localTok;
    // HEAL: nothing moved, but this synced template's baked assets are missing locally
    // (interrupted first hydration, cleared browser storage, fresh install restoring a
    // synced list). Cheap persisted-presence probe — no decode/decrypt per tick.
    let missingViews: string[] = [];
    if (!versionMoved && !contentMoved && localVer) {
      const savedViews = [...new Set(
        localRow.currentVersion?.payload?.views?.map((v) => v.view_type).filter(Boolean) ?? ["back"],
      )];
      const present = await Promise.all(savedViews.map((v) => hasMockupStored(localVer, v)));
      missingViews = savedViews.filter((_, i) => !present[i]);
      // Only heal when the local record says a bake EXISTS to download.
      if (missingViews.length > 0 && !hasAnyBakedView(localRow.currentVersion?.bakedAssets as BakedAssetRefsMap | null | undefined)) {
        // WS-P: the suppression is CORRECT — there is nothing on the cloud to fetch, so a
        // download would 404 forever. But blanking the list silently is how cause (i) (the bake
        // was never uploaded) became indistinguishable from "everything is in sync". Name it
        // once, then blank it exactly as before.
        announceBakeAbsence(log, "not-published", {
          templateId: localRow.templateId,
          versionId: localVer,
          templateName: localRow.name,
        });
        missingViews = [];
      }
    }
    if (!versionMoved && !contentMoved && missingViews.length === 0) continue; // in sync
    try {
      // The list row is a payload-less summary; the FULL record carries the baked refs.
      const full = await syncClient.getTemplate(row.templateId);
      await saveSyncedTemplate(storage, full); // authoritative local copy at the new version
      const refs = full.currentVersion?.bakedAssets as BakedAssetRefsMap | null | undefined;
      // Whether the ASSET download actually completed. Was a swallowed .catch() BEFORE
      // count++/breadcrumb, so a failed download still counted as "re-hydrated" and the
      // breadcrumb lied — every tick. The record save above stands either way.
      let assetsOk = true;
      // The REAL reason the download failed. `.catch(() => false)` used to discard it, so the
      // log below could only guess ("check R2 CORS / connectivity") — which is why a dead asset
      // ref, a 404 and an offline machine all produced the same undiagnosable line.
      let assetError: string | undefined;
      const versionId = full.currentVersion?.versionId;
      if (refs && versionId) {
        // Every view the version SAVED (its geometry's `view_type`s), not just the primary.
        const views = full.currentVersion.payload?.views?.map((v) => v.view_type).filter(Boolean) ?? [];
        const uniqueViews = [...new Set(views)];
        assetsOk = await hydrateBakedAssets(versionId, refs, ioHydrateDeps(storage), {
          views: uniqueViews.length > 0 ? uniqueViews : ["back"],
          // A content change under the SAME version id must overwrite the stale local copy;
          // a plain heal (assets merely missing) downloads without forcing.
          force: contentMoved,
        }).catch((e) => {
          assetError = e instanceof Error ? e.message : String(e);
          return false;
        });
      }
      if (assetsOk) {
        count++;
        clearAssetFailure(log, row.templateId);
        log?.breadcrumb("sync", "autoSync re-hydrated synced template", {
          templateId: row.templateId,
          reason: versionMoved ? "new version" : contentMoved ? "content changed (token)" : `missing local assets (${missingViews.join(",")})`,
          from: localVer || "(none)", to: freshVer || localVer,
        });
      } else {
        // ONE line per template per FAILURE STREAK, not per tick. The ticker runs every 60s and
        // on every window focus, so an asset that stays broken used to emit an identical error
        // for every template on every tick — hundreds of rows that bury everything else and say
        // nothing new. `noteAssetFailure` reports the first occurrence and then only on
        // escalation, and reports recovery when it finally succeeds.
        noteAssetFailure(log, row.templateId, {
          trigger: versionMoved ? "new version" : contentMoved ? "content changed (token)" : `missing local assets (${missingViews.join(",")})`,
          // `assetError` only ever catches a REJECTION, but `hydrateBakedAssets` also RESOLVES
          // `false` — and its commonest way of doing so is the memoized-failure short-circuit
          // (`bakedSync.ts:422`), which this 60s ticker hits almost every time because
          // `FAILURE_TTL_MS` is also 60s. On that path the real message was already stored
          // (`bakedSync.ts` copies the per-view failure onto the aggregate via `setFailure`),
          // so the log was printing "without an error" over an error the module was holding.
          // Ask for it. The fallback survives only for a `false` from a version we cannot key.
          error: assetError
            ?? (versionId ? hydrationFailure(versionId)?.message : undefined)
            ?? "download reported failure without an error",
        });
      }
    } catch (e) {
      log?.log("warn", "autoSync re-hydrate of a synced template failed", {
        templateId: row.templateId, error: e instanceof Error ? e.message : String(e),
      });
    }
  }
  return count;
}

/** Flip the sync-activity flag and notify the freshness indicator (only on a real change). */
function setSyncing(next: boolean): void {
  if (syncing === next) return;
  syncing = next;
  for (const cb of [...syncStateSubscribers]) {
    try {
      cb();
    } catch {
      /* a subscriber throw must never break the ticker or starve other subscribers */
    }
  }
}

/** Run one revalidation pass. Never throws — failures are logged and swallowed. */
async function runTick(reason: string): Promise<void> {
  if (!running || !client) return;
  if (ticking) {
    pendingTick = reason; // coalesce: re-run ONCE when the in-flight tick settles
    return;
  }
  const owner = tickOwner;
  ticking = true;
  setSyncing(true); // tick in flight → show the live activity indicator
  try {
    let fresh: ProductTemplate[];
    try {
      fresh = (await client.listTemplates()) ?? [];
      consecutiveTickFailures = 0; // a good tick re-arms the escalation below
    } catch (e) {
      // Offline / transient cloud error is NORMAL — keep the last-known list and let the
      // next tick retry. Never a silent swallow, but never a warn-per-minute either: an
      // offline hour used to write 60 warns into the Error view ("lots of errors"). The
      // first few failures are info breadcrumbs; one warn fires when the outage persists.
      consecutiveTickFailures++;
      const detail = { reason, consecutiveFailures: consecutiveTickFailures, error: e instanceof Error ? e.message : String(e) };
      if (consecutiveTickFailures === TICK_FAILURE_WARN_AFTER) {
        logger?.log("warn", `autoSync has failed ${TICK_FAILURE_WARN_AFTER} ticks in a row — offline or the cloud is unreachable (will keep retrying quietly)`, detail);
      } else {
        logger?.breadcrumb("sync", "autoSync tick failed (will retry next cycle)", detail);
      }
      return;
    }
    // `stopAutoSync()` may have run WHILE the fetch was in flight (logout mid-await). A stale
    // tick must not write freshness state, poison the change-detector, or deliver after logout.
    // Ownership, not just `running`: after a logout→login the module IS running again, so the
    // bare check waved the abandoned tick through and it published a list fetched under the
    // PREVIOUS session into the new one's freshness/change-detector state.
    if (!running || !client || owner !== tickOwner) return;
    lastSyncAt = Date.now();
    // Reconcile locally-synced templates' baked assets EVERY tick — before the fingerprint
    // gate, because the HEAL half (assets missing locally after a restart/cleared storage/
    // interrupted download) has nothing to do with the list changing. The probe is cheap
    // (persisted-presence getKey per synced view); downloads only happen when something is
    // actually stale or missing. Runs BEFORE notifying so a subscriber that re-reads local
    // state (the Mockup Generator) sees the freshly hydrated version.
    if (io) {
      try {
        await rehydrateChangedBaked(io, client, fresh, logger);
      } catch (e) {
        logger?.log("warn", "autoSync baked rehydrate pass failed", {
          error: e instanceof Error ? e.message : String(e),
        });
      }
    }
    const fp = fingerprintTemplates(fresh);
    if (fp === lastFingerprint) return; // list unchanged → freshness advances, no delivery
    const firstRun = lastFingerprint === null;
    lastFingerprint = fp;

    const delivery: TemplateSyncDelivery = { templates: fresh, at: lastSyncAt };
    for (const cb of [...subscribers]) {
      try {
        cb(delivery);
      } catch {
        /* a subscriber throw must never break the ticker or starve other subscribers */
      }
    }
    logger?.breadcrumb("sync", "autoSync delivered template change", {
      reason, count: fresh.length, firstRun,
    });
  } finally {
    // Only the ticker that TOOK the lock may release it. A tick abandoned by a stop (and by
    // then possibly running alongside the new ticker's own) would otherwise clear a lock and
    // a spinner it no longer owns, letting two ticks overlap.
    if (owner === tickOwner) {
      ticking = false;
      setSyncing(false); // tick settled (success, no-change, OR error) → hide the indicator
      const queued = pendingTick;
      pendingTick = null;
      if (queued && running && client) void runTick(queued);
    }
  }
}

// Focus / visibility handlers are module-stable so add/removeEventListener pair up.
function onFocus(): void {
  void runTick("focus");
}
function onVisibility(): void {
  if (typeof document !== "undefined" && document.visibilityState === "visible") {
    void runTick("visible");
  }
}

/**
 * Subscribe to delivered template-list changes. Returns an unsubscribe fn. Subscribers
 * persist across a stop/start (they belong to the mounted components, not the ticker).
 */
export function subscribeTemplates(cb: TemplateSubscriber): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/**
 * Start the ONE app-wide ticker (call after login). Idempotent while running — a second
 * call is ignored (single-instance guard), so wiring it from a session subscription that
 * may fire more than once is safe.
 */
export function startAutoSync(syncClient: AutoSyncClient, opts: StartAutoSyncOptions = {}): void {
  if (running) return; // single-instance guard
  running = true;
  client = syncClient;
  io = opts.io;
  logger = opts.logger;
  const intervalMs = opts.intervalMs ?? 60_000;

  if (typeof window !== "undefined") {
    window.addEventListener("focus", onFocus);
  }
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibility);
  }

  void runTick("start"); // immediate
  intervalId = setInterval(() => {
    void runTick("interval");
  }, intervalMs);
}

/**
 * Stop the ticker (call on logout). Removes the interval + focus/visibility listeners and
 * clears the change-detector so a later login re-delivers a fresh list. Subscribers are
 * left intact (the components own their own subscription lifecycle).
 */
export function stopAutoSync(): void {
  running = false;
  // Hand the lock to a fresh generation: whatever tick is still in flight now belongs to a
  // ticker that no longer exists, so the next login starts unlocked even if that tick never
  // answers (see `tickOwner`). The queued coalesced tick dies with its ticker too.
  tickOwner++;
  ticking = false;
  pendingTick = null;
  // Failure streaks are per-SESSION. Version ids are shared across accounts, so a streak left
  // behind by account A would suppress account B's first report of the same broken template.
  assetFailureStreak.clear();
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (typeof window !== "undefined") {
    window.removeEventListener("focus", onFocus);
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibility);
  }
  client = null;
  io = undefined;
  logger = undefined;
  lastFingerprint = null;
  lastSyncAt = null;
  setSyncing(false); // logout mid-tick must not strand the spinner (the stale tick stays false too)
}

/** Epoch-ms of the last SUCCESSFUL tick (changed or not); `null` before the first. */
export function getLastSyncAt(): number | null {
  return lastSyncAt;
}

/**
 * Stamp the freshness clock for a refresh the PAGE just did itself (WS-B B-6 / F-14).
 *
 * `SyncFreshness` read only `getLastSyncAt()`, which only the ticker moved — so pressing
 * Refresh on either template list updated the rows while the line beside the title still
 * said "Synced 4m ago", which is the one thing that line exists to be right about.
 *
 * This is a freshness STAMP, not a tick: it deliberately does NOT touch `lastFingerprint`
 * or the tick scheduling, so the next real tick still compares against the same fingerprint
 * and still fires on its own schedule. Subscribers are notified through the sync-state
 * channel `SyncFreshness` already listens on.
 */
export function noteManualSync(): void {
  lastSyncAt = Date.now();
  for (const cb of [...syncStateSubscribers]) {
    try {
      cb();
    } catch {
      /* a subscriber throw must never break the caller — same contract as setSyncing */
    }
  }
}

/** Whether a revalidation tick is currently in flight (drives the freshness spinner). */
export function getSyncing(): boolean {
  return syncing;
}

/**
 * Subscribe to `syncing` transitions (the live auto-sync activity indicator). Returns an
 * unsubscribe fn. Like `subscribeTemplates`, subscribers persist across stop/start — they
 * belong to the mounted freshness component, not the ticker.
 */
export function subscribeSyncState(cb: () => void): () => void {
  syncStateSubscribers.add(cb);
  return () => {
    syncStateSubscribers.delete(cb);
  };
}

/** Whether the ticker is currently running (for wiring guards / tests). */
export function isAutoSyncRunning(): boolean {
  return running;
}

/** Test-only: fully reset module state (ticker + subscribers). */
export function __resetAutoSyncForTests(): void {
  stopAutoSync();
  subscribers.clear();
  syncStateSubscribers.clear();
}
